import { Badge, Button, ConfirmDialog, Modal } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { formatDateTime } from '@/lib/formatters'
import { cn } from '@/lib/cn'
import type { SecurityLedgerTransaction } from '@/features/security-ledger/types/security-ledger.types'
import {
  useApproveRefund,
  useRejectRefund,
} from '@/features/security-ledger/hooks/useSecurityLedger'
import {
  TYPE_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  actorName,
  raisesBalance,
  signedLedgerNumber,
  formatLedgerNumber,
} from '@/features/security-ledger/lib/format'
import { useState } from 'react'

interface SecurityLedgerTableProps {
  companyId: string
  /** User document `_id` (same id used in ledger URLs). */
  ledgerUserId: string
  transactions: SecurityLedgerTransaction[]
  /** Effective currency for this company's ledger (shown in the amount header). */
  currency: string
  isLoading: boolean
  error: Error | null
  canApproveRefund: boolean
  canRejectRefund: boolean
  onRetry: () => void
}

export function SecurityLedgerTable({
  companyId,
  ledgerUserId,
  transactions,
  currency,
  isLoading,
  error,
  canApproveRefund,
  canRejectRefund,
  onRetry,
}: SecurityLedgerTableProps): React.ReactElement {
  const canDecideRefund = canApproveRefund || canRejectRefund
  const approveRefund = useApproveRefund()
  const rejectRefund = useRejectRefund()
  const [pendingApproveId, setPendingApproveId] = useState<string | null>(null)
  const [pendingRejectId, setPendingRejectId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  if (isLoading) {
    return (
      <SectionLoader variant="table" cols={7} rows={6} label="Loading security ledger entries" />
    )
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
        <p className="text-sm text-red-800 dark:text-red-300">{error.message}</p>
        <Button variant="secondary" className="mt-2" onClick={onRetry}>
          Retry
        </Button>
      </div>
    )
  }

  if (!transactions.length) {
    return (
      <p className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No transactions recorded yet.
      </p>
    )
  }

  const handleApproveConfirm = () => {
    if (!pendingApproveId) return
    approveRefund.mutate(
      { companyId, txMongoId: pendingApproveId, employeeId: ledgerUserId },
      { onSuccess: () => setPendingApproveId(null) },
    )
  }

  const closeReject = () => {
    setPendingRejectId(null)
    setRejectReason('')
  }

  const handleRejectConfirm = () => {
    if (!pendingRejectId) return
    rejectRefund.mutate(
      {
        companyId,
        txMongoId: pendingRejectId,
        employeeId: ledgerUserId,
        reason: rejectReason.trim() || undefined,
      },
      { onSuccess: closeReject },
    )
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Date</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Type</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Status</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                Amount ({currency})
              </th>
              <th className="px-4 py-3 text-right font-medium text-gray-500 dark:text-gray-400">Balance after</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">By</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">Note</th>
              {canDecideRefund && (
                <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
            {transactions.map((tx) => {
              const resolver = actorName(tx.resolvedBy)
              return (
                <tr key={tx._id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300">
                    {formatDateTime(tx.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                    {TYPE_LABEL[tx.type]}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANT[tx.status]}>{STATUS_LABEL[tx.status]}</Badge>
                  </td>
                  <td
                    className={cn(
                      'whitespace-nowrap px-4 py-3 text-right font-medium',
                      raisesBalance(tx.type)
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-red-700 dark:text-red-300',
                    )}
                  >
                    {signedLedgerNumber(tx.type, tx.amount)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                    {tx.balanceAfter != null ? formatLedgerNumber(tx.balanceAfter) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-400">
                    {actorName(tx.actorId)}
                    {tx.type === 'refund' && resolver !== '—' && (
                      <span className="block text-xs text-gray-400 dark:text-gray-500">
                        {tx.status === 'approved' ? 'Approved' : 'Rejected'} by {resolver}
                      </span>
                    )}
                  </td>
                  <td className="max-w-[200px] truncate px-4 py-3 text-gray-600 dark:text-gray-400">
                    {tx.note || '—'}
                  </td>
                  {canDecideRefund && (
                    <td className="px-4 py-3">
                      {tx.type === 'refund' && tx.status === 'pending_approval' && (
                        <div className="flex gap-2">
                          {canApproveRefund && (
                            <Button
                              type="button"
                              variant="primary"
                              size="sm"
                              onClick={() => setPendingApproveId(tx._id)}
                            >
                              Approve
                            </Button>
                          )}
                          {canRejectRefund && (
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => setPendingRejectId(tx._id)}
                            >
                              Reject
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!pendingApproveId}
        title="Approve refund"
        message="Are you sure you want to approve this refund? This will reduce the security balance immediately."
        confirmLabel="Approve"
        onConfirm={handleApproveConfirm}
        onClose={() => setPendingApproveId(null)}
        confirmLoading={approveRefund.isPending}
      />

      <Modal
        open={!!pendingRejectId}
        onClose={closeReject}
        title="Reject refund"
        footer={
          <div className="flex gap-2">
            <Button
              type="button"
              variant="danger"
              onClick={handleRejectConfirm}
              loading={rejectRefund.isPending}
            >
              {rejectRefund.isPending ? 'Rejecting…' : 'Reject request'}
            </Button>
            <Button type="button" variant="secondary" onClick={closeReject} disabled={rejectRefund.isPending}>
              Cancel
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Rejecting leaves the security balance unchanged. Add an optional reason for the audit
            log and the employee.
          </p>
          <div>
            <label
              htmlFor="reject-reason"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Reason (optional)
            </label>
            <textarea
              id="reject-reason"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Outstanding dues not cleared"
              className="box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

SecurityLedgerTable.displayName = 'SecurityLedgerTable'
